/**
 * "Is there a screenshot?" — asked once, on the way past.
 *
 * An incident with no evidence is the single biggest source of the round trip
 * that costs a day: IT reads it, cannot reproduce it, asks for a screenshot,
 * and waits. Asking at the moment of submitting is the only point where the
 * person still has the broken screen in front of them, so that is where the
 * question goes.
 *
 * The shape of the ask is the whole design, and it is deliberately *not* a
 * validation rule:
 *
 *   - It costs exactly **one extra press**. The first press shows this; the
 *     second sends, attachment or not.
 *   - It is **never a dead end**. Plenty of incidents genuinely have nothing to
 *     attach — a phone that will not charge, a door badge that stopped working
 *     — and a form that will not let those through is a form people route
 *     around by phoning instead, which loses the ticket entirely.
 *   - It asks **once per form**, not once per press. Someone who has already
 *     said "I have nothing" is not asked again for the rest of that form, even
 *     if they edit and resubmit.
 *
 * Deliberately absent from `/emergency`. A P1 is reported by someone standing
 * next to a stopped line; an extra press between them and IT is indefensible
 * there, whatever it buys on an ordinary incident.
 */

import { Icon, ImageUp } from "@norican/icons";
import { Alert } from "@norican/react";
import { useState } from "react";

import { useT } from "@/i18n";

export interface AttachmentNudge {
  /** True while the prompt should be on screen. */
  visible: boolean;
  /**
   * Call from the submit handler, *after* field validation has passed.
   *
   * Returns `true` when the submission should be held so the prompt can be
   * read — which happens at most once, and never when a file is already
   * attached. `false` means carry on and send.
   */
  hold: () => boolean;
}

/**
 * @param hasAttachment whether the form currently carries at least one file.
 */
export function useAttachmentNudge(hasAttachment: boolean): AttachmentNudge {
  const [asked, setAsked] = useState(false);

  return {
    visible: asked && !hasAttachment,
    hold: () => {
      if (hasAttachment || asked) {
        return false;
      }
      setAsked(true);
      return true;
    },
  };
}

export interface AttachmentNudgePromptProps {
  /** Names the action the second press performs, e.g. "Report this issue". */
  submitLabel: string;
}

/**
 * The prompt itself.
 *
 * `warning` rather than `info` for the tone that carries `role="alert"`: the
 * user pressed a send button and nothing was sent, and that has to be
 * announced rather than politely queued behind whatever else is on the page.
 */
export function AttachmentNudgePrompt({ submitLabel }: AttachmentNudgePromptProps) {
  const { t } = useT("tickets");

  return (
    <Alert
      tone="warning"
      title={t("attachmentNudge.title")}
      icon={<Icon icon={ImageUp} size="sm" tone="warning" />}
    >
      {t("attachmentNudge.body", { action: submitLabel })}
    </Alert>
  );
}
